import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  getAdditionalUserInfo
} from 'firebase/auth';
import { auth, googleProvider, db } from '@/integrations/firebase/client';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Phone, Chrome } from 'lucide-react';

const Auth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    verificationCode: ''
  });
  const [phoneStep, setPhoneStep] = useState<'phone' | 'code'>('phone');

  const grantWelcomeCredits = async (userId: string) => {
    try {
      const creditsRef = doc(db, 'users', userId, 'settings', 'credits');
      const creditsSnap = await getDoc(creditsRef);
      
      if (!creditsSnap.exists()) {
        await setDoc(creditsRef, {
          total: 50,
          freeCreditsAwarded: true,
          updatedAt: serverTimestamp(),
        });
        
        const balanceRef = doc(db, 'users', userId, 'settings', 'credits', 'balance', 'welcome_gift');
        await setDoc(balanceRef, {
          createdAt: serverTimestamp(),
          summary: {
            type: 'welcome_gift',
            creditsChange: 50,
            description: 'Welcome Gift: 50 free credits',
          },
        });
        
        toast.success(t('auth.welcomeGift'), {
          duration: 10000,
        });
      }
    } catch (error) {
      console.error('Error granting welcome credits:', error);
    }
  };

  const handleEmailAuth = async (isSignUp: boolean) => {
    setLoading(true);
    try {
      if (isSignUp) {
        const cred = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        await setDoc(
          doc(db, 'users', cred.user.uid),
          {
            uid: cred.user.uid,
            email: cred.user.email,
            providerId: 'password',
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
          },
          { merge: true }
        );
        await grantWelcomeCredits(cred.user.uid);
        toast.success(t('auth.signupSuccess'));
      } else {
        const cred = await signInWithEmailAndPassword(auth, formData.email, formData.password);
        await setDoc(
          doc(db, 'users', cred.user.uid),
          {
            uid: cred.user.uid,
            email: cred.user.email,
            providerId: 'password',
            lastLoginAt: serverTimestamp(),
          },
          { merge: true }
        );
        await grantWelcomeCredits(cred.user.uid);
        toast.success(t('auth.signinSuccess'));
      }
      navigate('/feedbacks');
    } catch (error: any) {
      toast.error(error.message || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const isNewUser = getAdditionalUserInfo(result)?.isNewUser;

      await setDoc(
        doc(db, 'users', user.uid),
        {
          uid: user.uid,
          email: user.email,
          providerId: user.providerData?.[0]?.providerId || 'google.com',
          lastLoginAt: serverTimestamp(),
          ...(isNewUser ? { createdAt: serverTimestamp() } : {})
        },
        { merge: true }
      );

      await grantWelcomeCredits(user.uid);
      toast.success(t('auth.signinSuccess'));
      navigate('/feedbacks');
    } catch (error: any) {
      toast.error(error.message || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneAuth = async () => {
    setLoading(true);
    try {
      if (phoneStep === 'phone') {
        // Initialize reCAPTCHA
        if (!(window as any).recaptchaVerifier) {
          (window as any).recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible'
          });
        }
        
        const confirmationResult = await signInWithPhoneNumber(
          auth, 
          formData.phone, 
          (window as any).recaptchaVerifier
        );
        (window as any).confirmationResult = confirmationResult;
        setPhoneStep('code');
        toast.success(t('auth.codeSent'));
      } else {
        const result = await (window as any).confirmationResult.confirm(formData.verificationCode);
        const user = result.user;
        const isNewUser = getAdditionalUserInfo(result)?.isNewUser;

        await setDoc(
          doc(db, 'users', user.uid),
          {
            uid: user.uid,
            email: user.email,
            providerId: 'phone',
            lastLoginAt: serverTimestamp(),
            ...(isNewUser ? { createdAt: serverTimestamp() } : {})
          },
          { merge: true }
        );
        await grantWelcomeCredits(user.uid);
        toast.success(t('auth.signinSuccess'));
        navigate('/feedbacks');
      }
    } catch (error: any) {
      toast.error(error.message || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen bg-hero-gradient overflow-hidden flex items-center justify-center p-4">
      <Helmet>
        <title>RoughCut AI — {t('auth.title')}</title>
        <meta name="description" content={t('auth.description')} />
      </Helmet>
      <div className="absolute inset-0 bg-overlay-dim" aria-hidden="true" />
      
      <Card className="relative z-10 w-full max-w-md shadow-2xl border-0 bg-background/80 backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <img src="/logo-big.png" alt="RoughCut AI Studio" className="h-28" />
          </div>
          <CardDescription>{t('auth.subtitle')}</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">{t('auth.signin')}</TabsTrigger>
              <TabsTrigger value="signup">{t('auth.signup')}</TabsTrigger>
            </TabsList>
            
            {/* Email/Password Auth */}
            <TabsContent value="signin" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={t('auth.passwordPlaceholder')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button 
                  onClick={() => handleEmailAuth(false)} 
                  disabled={loading}
                  className="w-full"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {loading ? t('auth.loading') : t('auth.signin')}
                </Button>
              </div>
            </TabsContent>
            
            <TabsContent value="signup" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="signup-email">{t('auth.email')}</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('auth.emailPlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder={t('auth.passwordPlaceholder')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <Button 
                  onClick={() => handleEmailAuth(true)} 
                  disabled={loading}
                  className="w-full"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {loading ? t('auth.loading') : t('auth.signup')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="space-y-4">
            <Separator />
            
            {/* Google Auth */}
            <Button 
              variant="outline" 
              onClick={handleGoogleAuth}
              disabled={loading}
              className="w-full"
            >
              <Chrome className="w-4 h-4 mr-2" />
              {t('auth.google')}
            </Button>
            
            
          </div>
          
          {/* reCAPTCHA container */}
          <div id="recaptcha-container"></div>
        </CardContent>
      </Card>
    </main>
  );
};

export default Auth;